import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, classes } from '../ui.tsx';
import type { MagicQuoteCoverage } from '@shared/types/index.ts';

interface MagicQuoteRoomCheckProps {
  coverage: MagicQuoteCoverage;
  canEdit: boolean;
  busy: boolean;
  manualQuotePath: string;
  onConfirm: (roomTemplateIds: string[]) => void;
}

const MAX_SUGGESTIONS = 6;

/**
 * Runtime authority for coverage-v1 UI actions. API types do not protect a
 * mixed-version browser from a newer or malformed Worker response, so every
 * action that can produce a quote shares this exact fail-closed check.
 */
export function isMagicQuoteCoverageProtocolValid(value: unknown): value is MagicQuoteCoverage {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const coverage = value as Record<string, unknown>;
  if (
    coverage.protocolVersion !== 1 ||
    !Number.isSafeInteger(coverage.version) ||
    (coverage.version as number) < 1 ||
    typeof coverage.confirmed !== 'boolean' ||
    typeof coverage.canConfirm !== 'boolean' ||
    !Array.isArray(coverage.gaps) ||
    coverage.gaps.some((gap) => typeof gap !== 'string' || gap.trim().length === 0) ||
    !Array.isArray(coverage.suggestions)
  ) {
    return false;
  }
  if (
    coverage.confirmed
      ? !coverage.canConfirm ||
        typeof coverage.confirmedAt !== 'string' ||
        !Number.isFinite(Date.parse(coverage.confirmedAt))
      : coverage.confirmedAt !== null
  ) {
    return false;
  }
  if (coverage.suggestions.length > MAX_SUGGESTIONS) return false;
  const ids: string[] = [];
  for (const item of coverage.suggestions) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
    const suggestion = item as Record<string, unknown>;
    if (typeof suggestion.roomTemplateId !== 'string' || suggestion.roomTemplateId.trim().length === 0) return false;
    if (typeof suggestion.name !== 'string' || suggestion.name.trim().length === 0) return false;
    ids.push(suggestion.roomTemplateId);
  }
  return new Set(ids).size === ids.length;
}

/**
 * Shared coverage-v1 review for the existing Magic Quote walkthrough.
 *
 * Suggestion ids are opaque library ids. Selection and confirmation leave in
 * one request, so there is no moment where the UI says the home is complete
 * but the selected kitchen or basement has not been inserted yet.
 */
export default function MagicQuoteRoomCheck({
  coverage,
  canEdit,
  busy,
  manualQuotePath,
  onConfirm,
}: MagicQuoteRoomCheckProps) {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => setSelected([]), [coverage.version]);

  const protocolError = !isMagicQuoteCoverageProtocolValid(coverage);

  return (
    <section className="card mb-6 border-[color:var(--color-brand)]" aria-labelledby="magic-room-check-heading">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="magic-room-check-heading" className="text-lg font-semibold">
            {t('magicQuote.review.roomCheckTitle')}
          </h2>
          <p className="text-sm text-[color:var(--color-muted)]">{t('magicQuote.review.roomCheckBody')}</p>
        </div>
        <span
          className={classes(
            'rounded-full border px-2 py-1 text-xs font-semibold',
            coverage.confirmed
              ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)]'
              : 'border-[color:var(--color-warning)]/50',
          )}
        >
          {coverage.confirmed ? t('magicQuote.review.roomCheckConfirmed') : t('magicQuote.review.roomCheckRequired')}
        </span>
      </div>

      {protocolError ? (
        <div className="mt-3">
          <Alert tone="error" title={t('magicQuote.review.roomCheckProtocolTitle')}>
            {t('magicQuote.review.roomCheckProtocolBody')}
          </Alert>
          {canEdit ? (
            <Link className="btn btn-secondary mt-3" to={manualQuotePath}>
              {t('magicQuote.review.roomCheckManualQuote')}
            </Link>
          ) : null}
        </div>
      ) : coverage.confirmed ? (
        <p className="mt-3 text-sm text-[color:var(--color-muted)]">
          {coverage.confirmedAt
            ? t('magicQuote.review.roomCheckConfirmedAt', {
                date: new Intl.DateTimeFormat(i18n.language, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                }).format(new Date(coverage.confirmedAt)),
              })
            : t('magicQuote.review.roomCheckConfirmedBody')}
        </p>
      ) : (
        <>
          {coverage.gaps.length > 0 ? (
            <div className="mt-3 rounded-md bg-[color:var(--color-warning-soft)] p-3">
              <h3 className="text-sm font-semibold">{t('magicQuote.review.roomCheckGaps')}</h3>
              <ul className="mt-1 list-disc pl-5 text-sm">
                {coverage.gaps.map((gap) => (
                  <li key={gap}>{gap}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {coverage.suggestions.length > 0 ? (
            <fieldset className="mt-4">
              <legend className="text-sm font-semibold">{t('magicQuote.review.roomCheckSuggestions')}</legend>
              <p className="mb-2 text-sm text-[color:var(--color-muted)]">
                {t('magicQuote.review.roomCheckSuggestionsHint')}
              </p>
              <div className="flex flex-wrap gap-2">
                {coverage.suggestions.map((suggestion) => {
                  const checked = selected.includes(suggestion.roomTemplateId);
                  return (
                    <label
                      key={suggestion.roomTemplateId}
                      className={classes(
                        'flex items-center gap-2 rounded-full border px-3 py-2 text-sm',
                        canEdit ? 'cursor-pointer' : 'cursor-default opacity-70',
                        checked
                          ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)]'
                          : 'border-[color:var(--color-line)]',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!canEdit || busy}
                        onChange={(event) =>
                          setSelected((current) =>
                            event.target.checked
                              ? [...new Set([...current, suggestion.roomTemplateId])]
                              : current.filter((id) => id !== suggestion.roomTemplateId),
                          )
                        }
                      />
                      <span>{suggestion.name}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ) : null}

          {!canEdit ? (
            <p className="mt-4 text-sm text-[color:var(--color-muted)]">{t('magicQuote.review.roomCheckReadOnly')}</p>
          ) : (
            <div className="mt-4">
              <Button
                variant="primary"
                busy={busy}
                disabled={protocolError || (!coverage.canConfirm && selected.length === 0)}
                onClick={() => onConfirm(selected)}
              >
                {selected.length > 0
                  ? t('magicQuote.review.roomCheckAddAndConfirm', { count: selected.length })
                  : t('magicQuote.review.roomCheckConfirm')}
              </Button>
              {!coverage.canConfirm && selected.length === 0 ? (
                <p className="mt-2 text-xs text-[color:var(--color-muted)]">
                  {t('magicQuote.review.roomCheckResolveFirst')}
                </p>
              ) : null}
              {!coverage.canConfirm ? (
                <Link className="btn btn-secondary mt-2" to={manualQuotePath}>
                  {t('magicQuote.review.roomCheckManualQuote')}
                </Link>
              ) : null}
            </div>
          )}
        </>
      )}
    </section>
  );
}
